import { ReactElement, useContext, useState } from 'react';
import * as tsClient from '@tsClient';
import useNoodleApi from '@hooks/useNoodleApi';
import { useToast } from '@hooks';
import TeamsContext from '@providers/Teams/TeamsContext';
import { ToastTypeVariants } from '@context/ToastContext';
import Modal from '@components/Modal';
import TextInput from '@components/FormFields/TextInput';
import EmailInput from '@components/FormFields/EmailInput';
import PhoneInput from '@components/DesignLibrary/Atoms/PhoneInput';
import Button from '@/components/DesignLibrary/Button';
import Spacer from '@components/Spacer';
import s from './CreateCompanyModal.module.scss';

type CreateCompanyModalProps = {
  onClose: () => void;
};

const CreateCompanyModal = ({ onClose }: CreateCompanyModalProps): ReactElement => {
  const addToast = useToast();
  const { creatorId } = useContext(TeamsContext);

  const [companyName, setCompanyName] = useState('');
  const [companyNameError, setCompanyNameError] = useState<string | null>(null);

  const [pointOfContact, setPointOfContact] = useState('');
  const [pointOfContactError, setPointOfContactError] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState<string | null>(null);

  const [hasAttemptedToSubmit, setHasAttemptedToSubmit] = useState(false);

  const { getData: createCompanyFn, fetchingState: isCreatingCompany } = useNoodleApi(tsClient.createCompany, {
    toastErrorMessage: error => [ToastTypeVariants.ERROR, error?.errors?.[0] || 'Error creating company. Please try again.'],
    toastOnError: true,
    toastSuccessMessage: () => [ToastTypeVariants.SUCCESS, 'Company created successfully!'],
  });

  const handleSubmit = async (): Promise<void> => {
    if (!creatorId) {
      addToast(ToastTypeVariants.ERROR, 'Unable to determine creator team. Please try refreshing the page or contact support.');
      return;
    }

    setHasAttemptedToSubmit(true);

    if (companyNameError || pointOfContactError || emailError || phoneNumberError) {
      return;
    }

    await createCompanyFn({
      companyName,
      creatorId,
      email,
      phoneNumber,
      pointOfContact,
    });

    onClose();
  };

  return (
    <Modal title="Create new company" onClose={onClose}>
      <div className={s.createCompanyForm}>
        <TextInput
          id="companyName"
          value={companyName}
          onChange={newCompanyName => setCompanyName(newCompanyName)}
          label="Company name"
          placeholder="Company name"
          required
          isTouched={hasAttemptedToSubmit}
          onError={setCompanyNameError}
          error={companyNameError}
        />

        <TextInput
          id="pointOfContact"
          value={pointOfContact}
          onChange={newPointOfContact => setPointOfContact(newPointOfContact)}
          label="Point of contact"
          placeholder="Point of contact"
          required
          isTouched={hasAttemptedToSubmit}
          onError={setPointOfContactError}
          error={pointOfContactError}
        />

        <EmailInput
          id="email"
          value={email}
          onChange={newEmail => setEmail(newEmail)}
          label="Email"
          placeholder="Email"
          required
          isTouched={hasAttemptedToSubmit}
          onError={setEmailError}
          error={emailError}
        />

        <PhoneInput
          id="phone"
          value={phoneNumber}
          onChange={setPhoneNumber}
          label="Phone"
          required
          isTouched={hasAttemptedToSubmit}
          onError={setPhoneNumberError}
          error={phoneNumberError}
        />

        <Spacer size={8} />

        <Button loading={isCreatingCompany.isFetching} variant="primary" size="md" fullWidth onClick={handleSubmit}>
          Save
        </Button>
      </div>
    </Modal>
  );
};

export default CreateCompanyModal;
